// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { NetworkSource } from "@lib/messaging/NetworkSource";
import { RpcError } from "@lib/messaging/rpc/RpcError";
import { RpcResult } from "@lib/messaging/rpc/RpcResult";
import { CancellationToken } from "@lib/utilities/CancellationToken";
import { PromiseCompletionSource } from "@lib/utilities/PromiseCompletionSource";
import { Err, Ok } from "@lib/utilities/Result";

type JsonRpcPendingCallDetails = {
  id: string;
  method: string;
  target: NetworkSource;
};

/**
 * Represents a JSON-RPC call that has not completed yet and provides methods for setting its result.
 * Exposes a promise which is provided to the consumer.
 */
export class JsonRpcPendingCall<T, E>
{
  private readonly promiseCompletionSource = new PromiseCompletionSource<Result<T, RpcError<E>>>();

  readonly id: string;
  readonly method: string;
  readonly target: NetworkSource;

  constructor(details: JsonRpcPendingCallDetails)
  {
    this.id = details.id;
    this.method = details.method;
    this.target = details.target;
  }

  get promise(): RpcResult<T, E>
  {
    return this.promiseCompletionSource.promise;
  }

  attachCancellation(cancellationToken: CancellationToken)
  {
    cancellationToken.register(() =>
    {
      this.fail(RpcError.canceled);
    });
  }

  cancelAfter(timeout: number)
  {
    setTimeout(() =>
    {
      this.fail(RpcError.canceled);
    }, timeout);
  }

  complete(result: any)
  {
    this.promiseCompletionSource.resolve(Ok(result));
  }

  fail(error: RpcError<any>)
  {
    this.promiseCompletionSource.resolve(Err(error));
  }
}